import type { LucideIcon } from 'lucide-react';
import {
  ArrowDown, ArrowDownUp, ArrowLeft, ArrowLeftRight, ArrowRight, ArrowUp, Banknote, Bell, Box, BriefcaseBusiness, Building2, CalendarClock, CalendarDays,
  CalendarPlus, ChartColumn, Check, ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, CircleHelp, Clock,
  Coins, Copy, Download, Ellipsis, Euro, Eye, EyeOff, FileText, GraduationCap, Handshake, Kanban, Landmark, LayoutDashboard,
  LayoutGrid, Link2, List, ListChecks, ListTodo, LockKeyhole, LogOut, Mail, Megaphone, MessageSquare,
  Moon, PenTool, Pencil, Percent, Pin, Plus, Receipt, ReceiptText, RefreshCw, Repeat, Search, Send, Settings, SlidersHorizontal,
  SquareCheckBig, Sun, Trash2, TriangleAlert, Undo2, Unlink, Upload, UserRound, Users, Wallet, X,
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
  /** Traccia il tratto come un fendente (stroke-draw) all'ingresso. Usato sul check di successo. */
  draw?: boolean;
}

export type IconName =
  | 'dashboard' | 'users' | 'megaphone' | 'cube' | 'palette' | 'talent' | 'brief'
  | 'invoice' | 'lock' | 'chart' | 'settings' | 'plus' | 'search' | 'bell' | 'chevronD'
  | 'chevronR' | 'chevron' | 'arrowLeft' | 'arrowRight' | 'arrowUp' | 'arrowDown'
  | 'export' | 'upload' | 'mail' | 'file' | 'link' | 'filter' | 'list' | 'grid'
  | 'kanban' | 'check' | 'x' | 'more' | 'eye' | 'eyeOff' | 'calendar' | 'alert'
  | 'logout' | 'pin' | 'euro' | 'send' | 'refresh' | 'sun' | 'moon' | 'help' | 'trash' | 'clock' | 'zap'
  | 'tasks' | 'checklist' | 'repeat' | 'copy' | 'message' | 'undo' | 'user' | 'calendarPlus' | 'handshake'
  | 'landmark' | 'wallet' | 'banknote' | 'receipt' | 'calendarClock' | 'coins' | 'swap' | 'building'
  | 'percent' | 'unlink' | 'pencil' | 'chevronU' | 'sort';

/**
 * Nome semantico -> glifo Lucide. I nomi restano quelli usati in tutta l'app
 * (config di Sidebar, newActions, CommandPalette): qui si cambia solo il disegno.
 */
const GLYPHS: Record<IconName, LucideIcon> = {
  // Navigazione principale
  dashboard: LayoutDashboard,   // Oggi
  users: Users,                 // CRM / clienti / utenti
  brief: BriefcaseBusiness,     // Professionisti / body rental
  megaphone: Megaphone,         // Digital Marketing
  cube: Box,                    // Digital Product / progetto
  palette: PenTool,             // Digital Design
  talent: GraduationCap,        // Talent Sourcing / Academy / candidati
  invoice: ReceiptText,         // Contabilità / fattura
  link: Link2,                  // Servizi gestiti / abbonamenti / integrazioni / collega
  lock: LockKeyhole,            // Vault / credenziali / permessi
  chart: ChartColumn,           // Report
  settings: Settings,           // Impostazioni

  // Azioni
  plus: Plus,
  search: Search,
  bell: Bell,
  export: Download,
  upload: Upload,
  mail: Mail,
  file: FileText,
  filter: SlidersHorizontal,
  check: Check,
  x: X,
  more: Ellipsis,
  eye: Eye,
  eyeOff: EyeOff,
  calendar: CalendarDays,
  alert: TriangleAlert,
  logout: LogOut,
  pin: Pin,
  euro: Euro,
  send: Send,
  refresh: RefreshCw,
  trash: Trash2,
  clock: Clock,
  help: CircleHelp,
  zap: ListTodo,                // "Da fare" (topbar + card Oggi)

  // Task
  tasks: SquareCheckBig,        // voce Sidebar "Task" / entità task
  checklist: ListChecks,        // checklist del task
  repeat: Repeat,               // ricorrenza
  copy: Copy,                   // copia link/URL
  message: MessageSquare,       // commenti
  undo: Undo2,                  // riapri
  user: UserRound,              // assegnatario
  calendarPlus: CalendarPlus,   // aggiungi a Google Calendar

  // Trattative
  handshake: Handshake,         // voce Sidebar "Trattative" / entità trattativa

  // Amministrazione
  landmark: Landmark,           // voce Sidebar "Amministrazione" / banca
  wallet: Wallet,               // liquidità
  banknote: Banknote,           // contanti
  receipt: Receipt,             // fattura ricevuta / fornitore
  calendarClock: CalendarClock, // scadenza fiscale
  coins: Coins,                 // incassi / rate
  swap: ArrowLeftRight,         // movimenti bancari
  building: Building2,          // fornitori
  percent: Percent,             // IVA / fisco
  unlink: Unlink,               // scollega un movimento
  pencil: Pencil,               // modifica inline

  // Viste
  list: List,
  grid: LayoutGrid,
  kanban: Kanban,

  // Direzionali
  chevronD: ChevronDown,
  chevronU: ChevronUp,
  chevronR: ChevronRight,
  chevron: ChevronsUpDown,
  sort: ArrowDownUp,            // Ordina per
  arrowLeft: ArrowLeft,
  arrowRight: ArrowRight,
  arrowUp: ArrowUp,
  arrowDown: ArrowDown,

  // Tema
  sun: Sun,
  moon: Moon,
};

export function Icon({ name, size = 14, className, strokeWidth = 1.75, draw }: IconProps) {
  const Glyph = GLYPHS[name];
  const cls = cn('inline-block', draw && 'icon-draw', className);
  if (!Glyph) {
    // Nome sconosciuto a runtime (config stantia): occupa lo spazio senza disegnare nulla.
    return <svg width={size} height={size} viewBox="0 0 24 24" className={cls} aria-hidden="true" />;
  }
  return (
    <Glyph
      size={size}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cls}
      aria-hidden="true"
    />
  );
}
